import { defineStore } from 'pinia'
import { ref } from 'vue'
import { apiGet } from '../composables/apiGet'
import { apiMutate } from '../composables/apiMutate'
import { useAsyncResource } from '../composables/useAsyncResource'
import { useChannelSubscription } from '../composables/useChannelSubscription'

// The retrieval-benchmark Tier 1 summary for the /admin/retrieval-bench
// view. Mirrors the JSON shape from
// `Api::V1::RetrievalBenchController#show`: the raw markdown of the
// committed `docs/retrieval_bench.md` report. The report's own H1 carries
// the run timestamp, so there's no separate generated-at field.
//
// A 404 (benchmark never run on this deploy) folds to `content: null`
// rather than an error, so the view can show a "not run yet" empty
// state instead of a red failure banner.
//
// `triggerRun()` POSTs to enqueue a RetrievalBenchJob; the job pushes a
// `RetrievalBenchChannel` ping on completion/failure. `running` stays
// true between the POST and that ping so the button shows a spinner.
// The on-disk report is the source of truth — a missed broadcast (WS
// drop) self-heals via the mount-time fetch AND the reconnect refetch,
// which also releases a stuck `running`.

interface RetrievalBenchPayload {
  content: string
}

// Broadcast payload from RetrievalBenchChannel.broadcast_status.
interface RunEvent {
  event: string
  status: 'completed' | 'failed'
}

export const useRetrievalBenchStore = defineStore('retrievalBench', () => {
  const running = ref(false)
  const runError = ref<string | null>(null)

  // Fetcher returns the content string directly so `data` aliases to
  // `content` — a writable ref, compatible with $patch in view specs
  // (same pattern as lint_notes returning `data: notes`).
  const {
    data: content,
    loaded,
    loading,
    error: loadError,
    refresh,
  } = useAsyncResource<string | null>(
    async () => {
      const payload = await apiGet<RetrievalBenchPayload>('/api/v1/retrieval_bench', {
        failureLabel: 'Failed to load retrieval benchmark',
        tolerateStatuses: [404],
      })
      return payload?.content ?? null
    },
    { onError: () => {} },
  )

  async function fetch() {
    await refresh()
  }

  // Enqueue a run. Keeps `running` true on success (the channel ping
  // clears it); only the enqueue failure (e.g. 403) clears it here and
  // surfaces via `runError`. Re-throws so the view can toast the error.
  async function triggerRun() {
    runError.value = null
    running.value = true
    try {
      await apiMutate('/api/v1/retrieval_bench', {
        method: 'POST',
        failureLabel: 'Failed to start benchmark',
      })
    } catch (err) {
      running.value = false
      runError.value = err instanceof Error ? err.message : String(err)
      throw err
    }
  }

  function applyRunEvent(data: RunEvent) {
    if (data.event !== 'run') return
    running.value = false
    if (data.status === 'completed') {
      void fetch()
    } else if (data.status === 'failed') {
      runError.value = 'Benchmark run failed — check the job logs.'
    }
  }

  function subscribe() {
    useChannelSubscription<RunEvent>('RetrievalBenchChannel', {
      received: applyRunEvent,
      // On a WS reconnect, clear `running` (a run that finished while
      // disconnected would otherwise strand the spinner) and re-fetch to
      // catch any completion broadcast missed during the drop. The
      // skip-first-connect guard is owned by useChannelSubscription's
      // refetchOnReconnect option.
      refetchOnReconnect() {
        running.value = false
        void fetch()
      },
    })
  }

  return {
    content,
    loaded,
    loadError,
    loading,
    running,
    runError,
    fetch,
    triggerRun,
    subscribe,
  }
})
